import { useRequest, apis } from "../utils";
import {
  AttachmentFile,
  SettlementCalculateData,
  SettlementidReq,
  SettlementUpdateData,
  SettlementViewerProps,
  SettlementViewerRef,
  TemplateidRes,
} from "./dto";
import { message, Modal, Spin, Tabs } from "@gw/web-basic-components";
import TableEditor, {
  isListValue,
  TableEditorConfig,
  TableViewerProps,
  TableViewerRef,
  VALUE_TYPE,
} from "./table-editor";
import React, {
  forwardRef,
  Fragment,
  ReactNode,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { produce } from "immer";
import { arabicToChinese, cn, isFunction, isNullOrUndefined } from "./utils";
import DotLabel from "./dot-label";
import { useLatest } from "ahooks";
import AttachmentUpload from "./attachment-upload";
import { IAttachmentUploadProps } from "./attachment-upload/dto";

const Viewer = forwardRef(
  (
    {
      id,
      children,
      renderHeader,
      className,
      style,
      editable,
      withAttachement = false,
      ...rest
    }: SettlementViewerProps,
    ref: Ref<SettlementViewerRef>
  ) => {
    // 表格实例
    const tableRef = useRef<TableViewerRef[]>([]);
    // 错误提示
    const [errors, setErrors] = useState<TableViewerProps["errors"][]>([]);
    // 结算单数据
    const [settlement, setSettlement] = useState<
      TableViewerProps["dataSource"]
    >([]);
    const [activeKey, setActiveKey] = useState(0);
    const [attachment, setAttachment] = useState<
      IAttachmentUploadProps["value"]
    >([]);
    // 是否在计算后保存
    const saveWithCalculateRef = useRef<() => void>();
    // 上一个计算的结算单key
    const prevCalculatingRef = useRef(0);
    // 获取模版详情
    const [{ loading: templateLoading, data: template }, fetchTemplateDetail] =
      useRequest<SettlementidReq, TemplateidRes[]>(
        apis.settlementTmpl.getTemplateBySettlementId
      );
    // 获取结算单详情
    const [
      { data: settlementDetail, loading: settlementLoading },
      fetchSettlementDetail,
    ] = useRequest<
      { id: string },
      { files: AttachmentFile[]; resps: TableViewerProps["dataSource"] }
    >(apis.settlementTmpl.getSettlementDetail);
    // 计算
    const [
      { loading: calculateLoading },
      fetchCalculateSettlement,
      calculateInstance,
    ] = useRequest<
      Omit<SettlementUpdateData, "files">,
      SettlementCalculateData
    >(apis.settlementTmpl.simulateCalculate);
    // 保存模版
    const [{ loading: saveLoading }, fetchSaveSettlement, saveInstance] =
      useRequest<SettlementUpdateData, SettlementUpdateData>(
        apis.settlementTmpl.data
      );
    // 正在计算（calculateLoading有闭包问题不准确，因此用这个在保存时判断）
    const calculatingRef = useLatest(calculateLoading);
    // 解析模版
    const configs = useMemo(() => {
      return (template ?? []).map((tmpl) => {
        if (tmpl?.templateContent) {
          try {
            return {
              id: tmpl.id,
              tmpl: JSON.parse(tmpl.templateContent) as TableEditorConfig,
              title: tmpl.templateName,
              img: tmpl.pngFileUrl,
            };
          } catch {
            return undefined;
          }
        }
        return undefined;
      });
    }, [template]);
    // 刷新
    const refresh = () => {
      if (!id) return;
      fetchTemplateDetail({ settlementId: id });
      fetchSettlementDetail({ id }).then((res) => {
        setSettlement(res.resps);
        setAttachment(
          res.files.map((i) => ({
            name: i.name,
            url: i.url,
            uid: i.key,
          }))
        );
      });
      setActiveKey(0);
      setErrors([]);
    };

    useEffect(() => {
      refresh();
    }, [id]);
    // 保存结算单数据
    const handleSave = async (current: number) => {
      if (prevCalculatingRef.current === current) {
        calculateInstance.abort();
      }
      saveInstance.abort();
      const values = tableRef.current[current]?.getFieldsValue();
      await fetchSaveSettlement({
        settlementId: id,
        settlementDetailParamList: values,
        files: attachment.map((i) => ({
          key: i.uid,
          name: i.name,
        })),
      });
      // 查询编辑后结算单图片地址
      await fetchTemplateDetail({ settlementId: id });
      const newSettlement = await fetchSettlementDetail({ id });
      setSettlement(newSettlement.resps);
      setAttachment(
        newSettlement.files.map((i) => ({
          name: i.name,
          url: i.url,
          uid: i.key,
        }))
      );
      setErrors([]);
      message.success("保存成功！");
      return values;
    };

    const handleCalculate = async (
      cfg: typeof configs[number],
      current: number,
      values: TableViewerProps["dataSource"]
    ) => {
      // 防止跨结算单取消计算
      if (prevCalculatingRef.current === current) {
        calculateInstance.abort();
      }
      prevCalculatingRef.current = current;
      fetchCalculateSettlement({
        settlementId: id,
        settlementDetailParamList: values,
      }).then((res) => {
        const newErrors: TableViewerProps["errors"] = [];
        for (const item of res.settlementDetailParamList) {
          // 公式字段
          const formula = cfg.tmpl.formulas.find((i) => i.code === item.code);
          // 是否为合法公式 多个字段or单个字段且不为自身
          const isVaildFormula =
            formula &&
            (formula.value.length > 1 ||
              (formula.value.length === 1 &&
                formula.value[0].value !== item.code));
          // 公式或者非数字类型则使用计算接口数据填充
          if (isVaildFormula || item.type !== VALUE_TYPE.NUMBER) {
            tableRef.current[activeKey]?.setFieldValue(
              item.code,
              isListValue(item) ? item.valueList : item.value
            );
          } else if (isListValue(item)) {
            item.validationPassedList?.forEach((i, idx) => {
              if (i === false) {
                newErrors.push({
                  path: [item.code, idx],
                  message: `数据结果应为 ${
                    item.valueList[idx] ?? "空"
                  }，请仔细核对`,
                  value: item.valueList[idx],
                });
              }
            });
          } else if (item.validationPassed === false) {
            newErrors.push({
              path: item.code,
              message: `数据结果应为 ${item.value ?? "空"}，请仔细核对`,
              value: item.value,
            });
          }
        }
        setErrors((preErrors) =>
          produce(preErrors, (draft) => {
            draft[current] = newErrors;
          })
        );
        // 恢复保存
        if (saveWithCalculateRef.current) {
          setTimeout(() => {
            saveWithCalculateRef.current();
            saveWithCalculateRef.current = undefined;
          });
        }
      });
    };

    const calculate: SettlementViewerRef["calculate"] = (
      values = [],
      current
    ) => {
      const keys = isNullOrUndefined(current)
        ? configs.map((_, idx) => idx)
        : [activeKey];
      const triggerCalculate = () => {
        keys.forEach((idx) => {
          handleCalculate(
            configs[idx],
            idx,
            tableRef.current?.[idx]?.getFieldsValue()
          );
        });
      };
      if (values.length === 0) {
        triggerCalculate();
      }
      for (const { path, value } of values) {
        tableRef.current?.forEach((r) => {
          r?.setFieldValue(path, value);
        });
      }
      setTimeout(() => triggerCalculate());
    };

    useImperativeHandle(ref, () => ({
      reset: () => {
        configs.forEach((_, idx) => {
          tableRef.current?.[idx]?.reset();
        });
        setErrors([]);
        // 重置附件
        if (settlementDetail) {
          setAttachment(
            settlementDetail.files.map((i) => ({
              name: i.name,
              url: i.url,
              uid: i.key,
            }))
          );
        }
      },
      calculate,
      save: async (current = activeKey) => {
        let retResolve: (
            value:
              | TableViewerProps["dataSource"]
              | PromiseLike<TableViewerProps["dataSource"]>
          ) => void,
          retReject: (reason?: Error) => void;
        const retPromise = new Promise<TableViewerProps["dataSource"]>(
          (resolve, reject) => {
            retResolve = resolve;
            retReject = reject;
          }
        );
        const save = () => {
          if (calculatingRef.current) {
            console.info("【SettlementEditor】正在计算，计算结束后自动保存");
            saveWithCalculateRef.current = () => {
              console.info("【SettlementEditor】计算结束，自动保存...");
              retResolve(handleSave(current));
            };
          } else {
            retResolve(handleSave(current));
          }
        };
        const saveWithConfirm = (msg: string) => {
          Modal.confirm({
            type: "warn",
            title: "",
            content: msg,
            onOk: save,
            onCancel: () => {
              retReject(new Error("【SettlementEditor】已取消保存"));
            },
          });
        };
        try {
          await tableRef.current[current]?.validateFields();
          // 正在保存
          if (saveLoading) {
            retReject(new Error("【SettlementEditor】正在保存..."));
          } else if (
            Array.isArray(errors[current]) &&
            errors[current].length > 0
          ) {
            saveWithConfirm("红色线框中的数据结果不一致，确定保存？");
          } else {
            save();
          }
        } catch {
          saveWithConfirm("红色线框中的数据未填写，确定保存？");
        }
        return retPromise;
      },
      refresh,
    }));

    // 是否为多配置
    const isMultiConfig = configs.length > 1;
    // 表格渲染
    const tables = (
      <>
        {configs.map((cfg, index) => (
          <Fragment key={cfg.id}>
            {cfg && settlement && (
              <div
                hidden={activeKey !== index}
                className="wbuc-flex wbuc-flex-col wbuc-gap-4"
              >
                {cfg.title && isMultiConfig && <DotLabel>{cfg.title}</DotLabel>}
                <img
                  className="wbuc-w-full"
                  hidden={editable}
                  src={cfg.img}
                  alt={cfg.title}
                />
                <TableEditor.Viewer
                  {...rest}
                  hidden={!editable}
                  editable
                  onChange={(_path, _value, values) =>
                    handleCalculate(cfg, index, values)
                  }
                  config={cfg.tmpl}
                  dataSource={settlement}
                  errors={errors[index]}
                  ref={(r) => (tableRef.current[index] = r)}
                />
              </div>
            )}
          </Fragment>
        ))}
        {withAttachement && (
          <>
            <DotLabel className="wbuc-my-5">上传附件</DotLabel>
            <AttachmentUpload
              readonly={!editable}
              tips={{
                label: "支持格式",
                content:
                  "jpg .png .doc .docx .pdf .xls .xlsx单个文件不能超过50MB.图片单张不超过10M",
              }}
              value={attachment}
              onChange={setAttachment}
            />
          </>
        )}
      </>
    );
    // 子元素渲染（支持外部传入额外渲染内容）
    const element = isFunction(children) ? children(tables) : tables;
    // 头部渲染
    const header = (node: ReactNode) => {
      return isFunction(renderHeader) ? renderHeader(node) : node;
    };

    return (
      <Spin
        spinning={templateLoading || settlementLoading || saveLoading}
        wrapperClassName="wbuc-size-full [&>.ant-spin-container]:wbuc-size-full"
      >
        {isMultiConfig ? (
          <div
            className={cn(
              "wbuc-flex wbuc-size-full wbuc-flex-col wbuc-overflow-hidden",
              className
            )}
            style={style}
          >
            {header(
              <Tabs
                items={configs.map((_, idx) => ({
                  key: idx.toString(),
                  label: `结算单${arabicToChinese(idx + 1)}`,
                }))}
                activeKey={activeKey.toString()}
                onChange={(k) => setActiveKey(Number(k))}
                className="wbuc-w-full wbuc-shrink-0 wbuc-px-[1.875rem] wbuc-pb-[0.625rem] wbuc-pt-5"
              />
            )}
            <div className="wbuc-flex-1 wbuc-overflow-auto wbuc-border wbuc-border-x-0 wbuc-border-b-0 wbuc-border-t wbuc-border-solid wbuc-border-[#0000000f] wbuc-p-5">
              {element}
            </div>
          </div>
        ) : (
          <div
            className={cn(
              "wbuc-size-full wbuc-overflow-auto wbuc-p-5",
              className
            )}
            style={style}
          >
            {header(<></>)}
            {element}
          </div>
        )}
      </Spin>
    );
  }
);

export default Viewer;
